/**
 * RPS-SURVEY-LIST
 * Liste des enquetes RPS eligibles du compte, avec la decision prise sur chacune. Le statut n'est
 * pas porte par la liste : il se lit dans accountRisksSettings.riskSurveys, seule source de verite,
 * editer ce champ suffit donc a rafraichir l'affichage.
 * Chargement, etat vide et actions sont partages entre la modale et la carte des reglages.
 */
import Button from "@/components/button"
import Link from "@/components/link"
import ListButton from "@/components/list-button"
import ModalConfirm from "@/components/modal-confirm"
import ToggleButton from "@/components/toggle-button"
import ListItem from "@/components/list-item"
import Space from "@/components/space"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import {
  accountFetchRpsSurveys,
  accountResetRpsSurveyStatuses,
  accountSetRpsSurveyStatus
} from "@/redux/account.actions"
import { getRpsSurveyStatus } from "@/redux/account.types"
import { formatDate } from "@/utils/format-date.utils"
import { faUndo } from "@fortawesome/free-solid-svg-icons"
import { useEffect, useState } from "react"

interface Props {
  className?: string
  onConfigure: (suggest?: boolean) => void
}

export default function RpsSurveyList(props: Props) {
  const dispatch = useAppDispatch()
  const accountName = useAppSelector((state) => state._session.accountName)
  const surveys = useAppSelector((state) => state.account.rpsSurveys)
  const riskSurveys = useAppSelector((state) => state._session.accountRisksSettings.riskSurveys)

  const [savingId, setSavingId] = useState<string | null>(null)
  const [isResetting, setIsResetting] = useState(false)
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    dispatch(accountFetchRpsSurveys())
  }, [dispatch])

  async function setActive(surveyId: string, active: boolean, suggest = false) {
    if (savingId) return
    setSavingId(surveyId)
    setError("")
    try {
      await dispatch(accountSetRpsSurveyStatus(surveyId, active ? "imported" : "ignored"))
      if (active) props.onConfigure(suggest)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Impossible de modifier l’enquête")
    } finally {
      setSavingId(null)
    }
  }

  //isInactive ne fait que griser le bouton : le verrou est ici
  function askReset() {
    if (!riskSurveys?.length || savingId || isResetting) return
    setIsResetConfirmOpen(true)
  }

  async function reset() {
    setIsResetConfirmOpen(false)
    setIsResetting(true)
    setError("")
    try {
      await dispatch(accountResetRpsSurveyStatuses())
    } catch (e) {
      setError(e instanceof Error ? e.message : "Impossible de réinitialiser les enquêtes")
    } finally {
      setIsResetting(false)
    }
  }

  if (!surveys.length) {
    return <div className="grey-t">Aucune enquête RPS clôturée sur ce compte.</div>
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-[20px] mb-[20px]">
        <p className="grey-t min-w-0 flex-1" style={{ margin: 0 }}>
          Enquêtes RPS {accountName} terminées
        </p>
        <div className="flex shrink-0 items-center gap-[10px]">
          <ListButton
            icon={faUndo}
            isInactive={!riskSurveys?.length || isResetting || Boolean(savingId)}
            text="Réinitialiser l'import"
            tooltipPosition="bottom"
            onClick={askReset}
          />
          <Link isWithoutMargin onClick={() => props.onConfigure(false)}>
            Associer les établissements
          </Link>
        </div>
      </div>
      {isResetConfirmOpen && (
        <ModalConfirm
          zIndex={999}
          onNo={() => setIsResetConfirmOpen(false)}
          onYes={reset}
          text="Les enquêtes repassent à « non défini » et la tâche à traiter réapparaît. L’association des établissements et des fonctions est conservée."
          textBold="Réinitialiser les enquêtes RPS ?"
        />
      )}

      {error && <p role="alert">{error}</p>}
      <div className={props.className}>
        {surveys.map((survey) => {
          const status = getRpsSurveyStatus(riskSurveys, survey.id)
          const active = status === "imported"

          return (
            //Hauteur de contenu fixe : la ligne ne change pas de taille selon qu'elle porte
            //deux boutons (49px) ou un interrupteur, et tout se centre verticalement
            <ListItem
              key={survey.id}
              className={`items-center min-h-[49px] ${status === "ignored" ? "opacity-50" : ""}`}>
              <div className="flex flex-col min-w-0">
                <div className="font-bold">{survey.name}</div>
                <div className="grey-t text-sm">
                  {survey.dateEnd ? `Clôturée le ${formatDate(survey.dateEnd, false, false)}` : "Clôturée"}
                </div>
              </div>

              <Space />

              <div className="flex shrink-0 items-center gap-[12px]">
                {!status ? (
                  <>
                    <Button
                      className="secondary"
                      disabled={Boolean(savingId)}
                      onClick={() => setActive(survey.id, false)}>
                      Ignorer
                    </Button>
                    <Button
                      className="primary"
                      disabled={Boolean(savingId)}
                      onClick={() => setActive(survey.id, true, true)}>
                      Associer
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="grey-t text-sm whitespace-nowrap">
                      {savingId === survey.id ? "Enregistrement…" : active ? "Activée" : "Ignorée"}
                    </span>
                    <ToggleButton
                      ariaLabel={`Activer l’enquête ${survey.name}`}
                      disabled={Boolean(savingId)}
                      isActive={active}
                      onToogle={() => setActive(survey.id, !active)}
                    />
                  </>
                )}
              </div>
            </ListItem>
          )
        })}
      </div>
    </>
  )
}
